/**
 * Post-login data pre-warm.
 *
 * Once the user signs in and we have a valid session, we proactively
 * pull the most-likely-needed data into Dexie so the app runs
 * offline from visit #1, not visit #2:
 *
 *   - Recent incidents (last 14 days for this agency) — so the
 *     dashboard is populated if the apparatus drops signal immediately.
 *   - Active ePCRs for the signed-in user — so a half-finished report
 *     can be picked up without network.
 *
 * Everything here is best-effort. Network failures, RLS blocks, and
 * missing tables are all caught and logged; the user never sees a
 * preload error because it's invisible work.
 *
 * Runs at most once per 6 hours per agency (unless forced) to keep
 * the cost sane on metered cellular connections.
 */

import { supabase } from './supabase';
import { db, withSyncMeta } from '@/offline/db';

const LAST_RUN_KEY = 'emspcr-preload-last-at';
const PRELOAD_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h
const RECENT_DAYS = 14;

type Maybe<T> = T | null | undefined;

function wasRecentlyRun(): boolean {
  try {
    const raw = localStorage.getItem(LAST_RUN_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return (Date.now() - ts) < PRELOAD_INTERVAL_MS;
  } catch { return false; }
}

function markRun(): void {
  try { localStorage.setItem(LAST_RUN_KEY, String(Date.now())); } catch { /* ignore */ }
}

export interface PreloadResult {
  incidents: number;
  epcrs: number;
  skipped: boolean;
  durationMs: number;
  errors: string[];
}

/**
 * Kick off the pre-warm. Non-blocking; the caller fires it after sign-in
 * and moves on. Returns the result for logging/debug if awaited.
 */
export async function preloadAfterSignIn(opts?: { force?: boolean }): Promise<PreloadResult> {
  const start = Date.now();
  const result: PreloadResult = { incidents: 0, epcrs: 0, skipped: false, durationMs: 0, errors: [] };

  if (!opts?.force && wasRecentlyRun()) {
    result.skipped = true;
    result.durationMs = Date.now() - start;
    return result;
  }

  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) {
      result.errors.push('no signed-in user');
      return result;
    }

    const { data: profile } = await supabase
      .from('user_profiles')
      .select('id, agency_id, role')
      .eq('id', userId)
      .maybeSingle();

    const agencyId = profile?.agency_id;
    if (!agencyId) {
      result.errors.push('profile has no agency_id');
      return result;
    }

    // 1. Recent incidents — dashboard + handoff context
    try {
      const since = new Date(Date.now() - RECENT_DAYS * 86_400_000).toISOString();
      const { data: incidents } = await supabase
        .from('incidents')
        .select('*')
        .eq('agency_id', agencyId)
        .gte('dispatch_time', since)
        .order('dispatch_time', { ascending: false })
        .limit(250);

      if (incidents?.length) {
        // Merge into Dexie preserving any local _dirty rows — we only
        // overwrite records that are already synced.
        const existing = await db.incidents.bulkGet(incidents.map((i) => i.id));
        const writes = incidents
          .map((inc: { id: string }, idx: number) => {
            const local = existing[idx] as Maybe<{ _dirty?: boolean }>;
            if (local?._dirty) return null; // leave local unsent work alone
            return withSyncMeta(inc as object, true);
          })
          .filter((v): v is NonNullable<typeof v> => v !== null);
        if (writes.length) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await db.incidents.bulkPut(writes as any);
        }
        result.incidents = writes.length;
      }
    } catch (e) {
      result.errors.push(`incidents: ${(e as Error).message}`);
    }

    // 2. Active ePCRs — anything the user created + open runs
    try {
      const { data: epcrs } = await supabase
        .from('epcrs')
        .select('*')
        .eq('agency_id', agencyId)
        .eq('locked', false)
        .order('updated_at', { ascending: false })
        .limit(50);

      if (epcrs?.length) {
        const existing = await db.epcrs.bulkGet(epcrs.map((e: { id: string }) => e.id));
        const writes = epcrs
          .map((ep: { id: string }, idx: number) => {
            const local = existing[idx] as Maybe<{ _dirty?: boolean }>;
            if (local?._dirty) return null;
            return withSyncMeta(ep as object, true);
          })
          .filter((v): v is NonNullable<typeof v> => v !== null);
        if (writes.length) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await db.epcrs.bulkPut(writes as any);
        }
        result.epcrs = writes.length;
      }
    } catch (e) {
      result.errors.push(`epcrs: ${(e as Error).message}`);
    }

    markRun();
  } catch (e) {
    result.errors.push(`top-level: ${(e as Error).message}`);
  } finally {
    result.durationMs = Date.now() - start;
    const dev = (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true;
    if (dev || result.errors.length > 0) {
      // eslint-disable-next-line no-console
      console.log('[preload]', result);
    }
  }

  return result;
}
